/** Regroup the WBS nodes by the Takt section they currently map to, for the
 *  "Sections" mapping view. Each section is a FLAT list of its task-holding
 *  nodes; every node carries its WBS ancestry (root excluded) so the row can
 *  show a ">"-delimited breadcrumb instead of nesting. Pure. */
import { naturalCompare } from "../../../../utils/sort";
import type { TaktNodeMapNode, TaktNodeMapping } from "../../../../models";

export type Pending = Record<number, Partial<TaktNodeMapping>>;

export interface SectionNodeRow {
  node: TaktNodeMapNode;
  /** WBS ancestry excluding the project root, ending in the node itself —
   *  rendered as `A > B > Node`. */
  path: string[];
}
export interface SectionGroup {
  /** "" → the "Not mapped" group (nodes with no section yet). */
  section: string;
  nodeCount: number;
  taskCount: number;
  rows: SectionNodeRow[];
}

const UNMAPPED = "";

/** Group nodes that hold tasks by their EFFECTIVE mapping (committed value with
 *  any staged `pending` edit overlaid), so re-sectioning a node moves it live.
 *  Sections sort alphabetically with "Not mapped" pinned last; rows within a
 *  section sort by their WBS path so siblings stay adjacent. */
export function groupNodesBySection(
  nodes: TaktNodeMapNode[],
  pending: Pending,
): SectionGroup[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const isRoot = (n: TaktNodeMapNode) =>
    n.parent_id == null || !byId.has(n.parent_id);

  // Walk parent_id to the root, then drop the root → [..ancestors, node].
  const pathOf = (n: TaktNodeMapNode): string[] => {
    const chain: TaktNodeMapNode[] = [];
    const seen = new Set<number>();
    let cur: TaktNodeMapNode | undefined = n;
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      chain.push(cur);
      if (isRoot(cur)) break;
      cur = byId.get(cur.parent_id as number);
    }
    chain.reverse(); // [root, ..., node]
    const trimmed = chain.length > 1 ? chain.slice(1) : chain; // drop root
    return trimmed.map((x) => x.name);
  };

  const sec = (n: TaktNodeMapNode) =>
    ((pending[n.id]?.section_name ?? n.mapping.section_name) || "").trim() || UNMAPPED;

  const sections = new Map<string, SectionNodeRow[]>();
  for (const n of nodes) {
    if (n.counts.tasks <= 0) continue; // only nodes that hold tasks are mappable
    const key = sec(n);
    const rows = sections.get(key) ?? [];
    sections.set(key, rows);
    rows.push({ node: n, path: pathOf(n) });
  }

  const out: SectionGroup[] = [];
  const secNames = Array.from(sections.keys()).sort((a, b) => {
    if (a === UNMAPPED) return 1; // "Not mapped" last
    if (b === UNMAPPED) return -1;
    return naturalCompare(a, b);
  });
  for (const name of secNames) {
    const rows = sections.get(name)!;
    rows.sort((a, b) => naturalCompare(a.path.join(" > "), b.path.join(" > ")));
    const taskCount = rows.reduce((s, r) => s + r.node.counts.tasks, 0);
    out.push({ section: name, nodeCount: rows.length, taskCount, rows });
  }
  return out;
}
